import { ENTITIES } from '../../constants/customSortFieldConfigs';
import type { EntityReportAdapter } from './EntityReportAdapter';
import { shortlistEntityReportAdapter } from './shortlistEntityReportAdapter';
import { submissionEntityReportAdapter } from './submissionEntityReportAdapter';
import { workspaceEntityReportAdapter } from './workspaceEntityReportAdapter';

const adaptersByKey = new Map<string, EntityReportAdapter>();
let defaultAdaptersRegistered = false;

const validEntities = new Set<string>(Object.values(ENTITIES));

const validateAdapterEntity = (adapter: EntityReportAdapter) => {
    if (!adapter.entity) {
        throw new Error(`EntityReportAdapter \"${adapter.key}\" must define an entity.`);
    }

    if (!validEntities.has(adapter.entity)) {
        throw new Error(
            `EntityReportAdapter \"${adapter.key}\" has invalid entity \"${adapter.entity}\". ` +
                `Allowed values: ${Array.from(validEntities).join(', ')}`
        );
    }
};

export const registerEntityReportAdapter = (adapter: EntityReportAdapter) => {
    validateAdapterEntity(adapter);
    adaptersByKey.set(adapter.key, adapter);
};

const ensureDefaultAdaptersRegistered = () => {
    if (defaultAdaptersRegistered) {
        return;
    }

    registerEntityReportAdapter(workspaceEntityReportAdapter);
    registerEntityReportAdapter(submissionEntityReportAdapter);
    registerEntityReportAdapter(shortlistEntityReportAdapter);
    defaultAdaptersRegistered = true;
};

export const getEntityReportAdapterByKey = (adapterKey?: string): EntityReportAdapter | undefined => {
    ensureDefaultAdaptersRegistered();

    if (!adapterKey) {
        return undefined;
    }

    return adaptersByKey.get(adapterKey);
};

type ResolveEntityReportAdapterParams = {
    adapter?: EntityReportAdapter;
    adapterKey?: string;
};

export const resolveEntityReportAdapter = ({
    adapter,
    adapterKey
}: ResolveEntityReportAdapterParams): EntityReportAdapter | undefined => {
    ensureDefaultAdaptersRegistered();

    if (adapter) {
        validateAdapterEntity(adapter);
        return adapter;
    }

    const resolvedByKey = getEntityReportAdapterByKey(adapterKey);
    if (resolvedByKey) {
        return resolvedByKey;
    }

    return undefined;
};
